/**
 * System Health page (formerly Dev Tools).
 *
 * Shows a live per-category status board for the platform:
 *
 * • Core    → backend + MongoDB
 * • Providers → in-memory storage/SMS/email/PDF/geocoding adapters
 * • Integrations → 3rd-party connectors saved in `integration_configs`
 * • Communications → active templates/triggers + last-24h dispatch count
 * • Data → high-level record counts
 *
 * Plus stub SMS/email inspectors carried over from the old Dev Tools page
 * (still helpful in non-production environments for tracing OTP codes and
 * dispatched notifications).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Activity, RefreshCw, Server, Database, Plug, MessageSquareText, HardDrive,
  CheckCircle2, AlertCircle, XCircle, Circle, HelpCircle,
} from 'lucide-react';

interface HealthCheck {
  category: string;
  name: string;
  kind: string;
  status: 'up' | 'warn' | 'down' | 'off' | 'unknown';
  detail: string;
  enabled?: boolean;
  connection_status?: string;
  last_test_at?: string | null;
  metric?: number;
  counts?: Record<string, number>;
  active_provider?: string;
}
interface HealthPayload {
  timestamp: string;
  environment: string;
  overall: 'up' | 'warn' | 'down' | 'unknown';
  checks: HealthCheck[];
  summary: Record<'up' | 'warn' | 'down' | 'off' | 'unknown', number>;
}
interface SmsRow { id: string; to: string; message: string; timestamp: string }
interface EmailRow { id: string; to: string; template_id: string; variables: Record<string, unknown>; timestamp: string }

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  Core: <Server size={16} />,
  Providers: <HardDrive size={16} />,
  Integrations: <Plug size={16} />,
  Communications: <MessageSquareText size={16} />,
  Data: <Database size={16} />,
};

const STATUS_META: Record<HealthCheck['status'], { icon: React.ReactNode; variant: 'success' | 'warning' | 'destructive' | 'muted'; label: string; color: string }> = {
  up:      { icon: <CheckCircle2 size={14} />, variant: 'success',     label: 'Healthy',       color: 'text-emerald-600' },
  warn:    { icon: <AlertCircle  size={14} />, variant: 'warning',     label: 'Attention',     color: 'text-amber-600' },
  down:    { icon: <XCircle      size={14} />, variant: 'destructive', label: 'Down',          color: 'text-red-600' },
  off:     { icon: <Circle       size={14} />, variant: 'muted',       label: 'Not enabled',   color: 'text-raptor-navy/40' },
  unknown: { icon: <HelpCircle   size={14} />, variant: 'muted',       label: 'Unknown',       color: 'text-raptor-navy/50' },
};

export const SystemHealthPage: React.FC = () => {
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const [sms, setSms] = useState<SmsRow[]>([]);
  const [email, setEmail] = useState<EmailRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const [h, s, e] = await Promise.all([
        api.get<HealthPayload>('/system-health'),
        api.get<SmsRow[]>('/dev-tools/sms').catch(() => ({ data: [] as SmsRow[] })),
        api.get<EmailRow[]>('/dev-tools/email').catch(() => ({ data: [] as EmailRow[] })),
      ]);
      setHealth(h.data);
      setSms(s.data ?? []);
      setEmail(e.data ?? []);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); const t = setInterval(() => void load(), 30_000); return () => clearInterval(t); }, []);

  const grouped = useMemo(() => {
    const g: Record<string, HealthCheck[]> = {};
    (health?.checks ?? []).forEach((c) => { (g[c.category] ??= []).push(c); });
    return g;
  }, [health]);

  const overallMeta = health ? STATUS_META[health.overall] : STATUS_META.unknown;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="system-health-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide flex items-center gap-2">
            <Activity size={26} className="text-raptor-blue" /> System Health
          </h2>
          <p className="text-sm text-raptor-navy/60 font-body">
            Live status of every service, provider and integration RAPTOR is using.
            Auto-refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-raptor-navy/50 font-body">
              Last refreshed {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => void load()} data-testid="refresh-health-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>
      </div>

      {/* Overall summary card */}
      {health && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full bg-raptor-neutral/50 ${overallMeta.color}`}>{overallMeta.icon}</div>
                <div>
                  <div className="font-headline text-xl text-raptor-navy" data-testid="overall-status">
                    {overallMeta.label}
                  </div>
                  <div className="text-xs text-raptor-navy/60 font-body">
                    Environment · <span className="font-tabular">{health.environment}</span> · reported {new Date(health.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(['up', 'warn', 'down', 'off', 'unknown'] as const).map((k) => {
                  if (!health.summary[k]) return null;
                  return (
                    <Badge key={k} variant={STATUS_META[k].variant} className="gap-1" data-testid={`summary-${k}`}>
                      {STATUS_META[k].icon} {health.summary[k]} {STATUS_META[k].label}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouped category cards */}
      {Object.entries(grouped).map(([category, checks]) => (
        <Card key={category} data-testid={`health-category-${category}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {CATEGORY_ICON[category] ?? <Circle size={16} />}
              {category}
              <Badge variant="muted" className="text-[10px]">{checks.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {checks.map((c, i) => {
                const meta = STATUS_META[c.status];
                return (
                  <div
                    key={`${c.kind}-${i}`}
                    className="p-3 rounded-md border border-border/60 bg-white flex flex-col gap-1"
                    data-testid={`health-check-${c.kind}-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-raptor-navy text-sm truncate">{c.name}</div>
                      <Badge variant={meta.variant} className="gap-1 text-[10px] shrink-0">
                        {meta.icon}{meta.label}
                      </Badge>
                    </div>
                    <div className="text-xs text-raptor-navy/70 font-body break-words">
                      {c.detail || '—'}
                    </div>
                    {c.enabled !== undefined && (
                      <div className="text-[10px] text-raptor-navy/50 font-body">
                        Enabled: {c.enabled ? 'yes' : 'no'}
                        {c.connection_status && ` · Status: ${c.connection_status}`}
                        {c.last_test_at && ` · Tested ${new Date(c.last_test_at).toLocaleString()}`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Dev stubs (kept from old DevTools) */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent stub SMS · {sms.length}</CardTitle>
            <CardDescription>OTP codes surface here when the SMS provider is not connected (Panacea disabled = stub logger).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-96 overflow-auto">
            {sms.length === 0 && <div className="text-sm text-raptor-navy/50">No SMS captured yet.</div>}
            {sms.map((s) => (
              <div key={s.id} className="p-3 bg-raptor-neutral/40 rounded-md" data-testid={`stub-sms-${s.id}`}>
                <div className="text-[11px] text-raptor-navy/50 font-tabular">{s.timestamp}</div>
                <div className="font-tabular text-sm text-raptor-navy">to {s.to}</div>
                <div className="text-sm text-raptor-navy/80 font-body">{s.message}</div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent stub Email · {email.length}</CardTitle>
            <CardDescription>Email is currently rendered + logged (real provider integration pending).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-96 overflow-auto">
            {email.length === 0 && <div className="text-sm text-raptor-navy/50">No email captured yet.</div>}
            {email.map((e) => (
              <div key={e.id} className="p-3 bg-raptor-neutral/40 rounded-md" data-testid={`stub-email-${e.id}`}>
                <div className="text-[11px] text-raptor-navy/50 font-tabular">{e.timestamp}</div>
                <div className="font-tabular text-sm text-raptor-navy">to {e.to} · template {e.template_id}</div>
                <div className="text-xs text-raptor-navy/70 font-body break-all">{JSON.stringify(e.variables)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Preserve the old export name so legacy imports (App.tsx) keep working
// during transition without a full file-move commit.
export const DevToolsPage = SystemHealthPage;
export default SystemHealthPage;
